import React from "react";
import { FilterDropdown, FilterConfig, FilterOption } from "@/components/ui/filter-dropdown";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";

interface FilterTravelRequestButtonProps {
  filters: FilterConfig[];
  onFiltersChange: (filters: FilterConfig[]) => void;
  options: FilterOption[];
  travelRequests: TravelRequest[];
  onFilteredTravelRequestsChange: (travelRequests: TravelRequest[]) => void;
  disabled?: boolean;
}

export const FilterTravelRequestButton: React.FC<FilterTravelRequestButtonProps> = ({
  filters,
  onFiltersChange,
  options,
  travelRequests,
  onFilteredTravelRequestsChange,
  disabled = false,
}) => {
  // Funzione di filtraggio
  const applyFilters = (travelRequestsToFilter: TravelRequest[], filtersToApply: FilterConfig[]) => {
    if (filtersToApply.length === 0) return travelRequestsToFilter;
    
    return travelRequestsToFilter.filter(travelRequest => {
      return filtersToApply.every(filter => {
        const { field, operation, value } = filter;
        
        switch (field) {
          case 'destinations': {
            const destinations = travelRequest.destinations.join(', ').toLowerCase();
            return operation === 'contains' ? destinations.includes(value.toLowerCase()) : false;
          }
          
          case 'customer_name': {
            const customerName = `${travelRequest.contact_first_name} ${travelRequest.contact_last_name}`.toLowerCase();
            return operation === 'contains' ? customerName.includes(value.toLowerCase()) : false;
          }
          
          case 'budget': {
            const budget = travelRequest.budget_max || 0;
            const filterValue = parseInt(value);
            switch (operation) {
              case 'equals': return budget === filterValue;
              case 'greater_than': return budget > filterValue;
              case 'less_than': return budget < filterValue;
              case 'greater_equal': return budget >= filterValue;
              case 'less_equal': return budget <= filterValue;
              default: return false;
            }
          }
          
          case 'guests': {
            const guests = travelRequest.total_guests || 0;
            const guestValue = parseInt(value);
            switch (operation) {
              case 'equals': return guests === guestValue;
              case 'greater_than': return guests > guestValue;
              case 'less_than': return guests < guestValue;
              case 'greater_equal': return guests >= guestValue;
              case 'less_equal': return guests <= guestValue;
              default: return false;
            }
          }
          
          case 'departure': {
            const departure = travelRequest.departure_point?.toLowerCase() || '';
            return operation === 'contains' ? departure.includes(value.toLowerCase()) : false;
          }
          
          case 'travel_start_date': {
            if (!filter.dateRange) return true;
            const travelRequestDate = travelRequest.travel_start_date ? new Date(travelRequest.travel_start_date).getTime() : 0;
            const fromDate = filter.dateRange.from ? new Date(filter.dateRange.from).getTime() : 0;
            const toDate = filter.dateRange.to ? new Date(filter.dateRange.to).getTime() : Number.MAX_SAFE_INTEGER;
            
            return travelRequestDate >= fromDate && travelRequestDate <= toDate;
          }
          
          case 'travel_end_date': {
            if (!filter.dateRange) return true;
            const travelRequestDate = travelRequest.travel_end_date ? new Date(travelRequest.travel_end_date).getTime() : 0;
            const fromDate = filter.dateRange.from ? new Date(filter.dateRange.from).getTime() : 0;
            const toDate = filter.dateRange.to ? new Date(filter.dateRange.to).getTime() : Number.MAX_SAFE_INTEGER;
            
            return travelRequestDate >= fromDate && travelRequestDate <= toDate;
          }
          
          case 'created_at': {
            if (!filter.dateRange) return true;
            const travelRequestDate = new Date(travelRequest.created_at).getTime();
            const fromDate = filter.dateRange.from ? new Date(filter.dateRange.from).getTime() : 0;
            const toDate = filter.dateRange.to ? new Date(filter.dateRange.to).getTime() : Number.MAX_SAFE_INTEGER;
            
            return travelRequestDate >= fromDate && travelRequestDate <= toDate;
          }
          
          default:
            return true;
        }
      });
    });
  };

  const handleFiltersChange = (newFilters: FilterConfig[]) => {
    onFiltersChange(newFilters);
    const filteredTravelRequests = applyFilters(travelRequests, newFilters);
    onFilteredTravelRequestsChange(filteredTravelRequests);
  };

  return (
    <FilterDropdown
      filters={filters}
      onFiltersChange={handleFiltersChange}
      options={options}
      disabled={disabled}
    />
  );
};
